// jsPDF est chargé dynamiquement dans generatePDF() pour réduire le bundle initial (~500 KB)
import { PHOTO_SLOTS } from '../utils/constants'

// ── Constantes ────────────────────────────────────────────────────────────────
// Le fuel est désormais stocké en % (0-100)


// ── Palette couleurs ──────────────────────────────────────────────────────────
const COLORS = {
  primary:   [30,  64,  175],
  success:   [34,  197, 94 ],
  warning:   [249, 115, 22 ],
  danger:    [220, 38,  38 ],
  white:     [255, 255, 255],
  lightGray: [240, 240, 240],
  midGray:   [180, 180, 180],
  darkGray:  [50,  50,  50 ],
  textLight: [100, 100, 100],
  rowEven:   [248, 250, 252],
}

// ── Helpers ───────────────────────────────────────────────────────────────────

// ✅ Résout dataUrl depuis objet dual-version ou string legacy
function resolveDataUrl(photo) {
  if (!photo) return null
  if (typeof photo === 'string') return photo
  return photo.dataUrl ?? null
}

// ✅ Détecte le format image réel pour éviter double compression
function getImgFormat(dataUrl) {
  if (!dataUrl) return 'JPEG'
  if (dataUrl.startsWith('data:image/png'))  return 'PNG'
  if (dataUrl.startsWith('data:image/webp')) return 'WEBP'
  return 'JPEG'
}

// ✅ Formate date/heure en français
function fmtDT(d) {
  if (!d) return '\u2014'
  try {
    return new Date(d).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  } catch { return '\u2014' }
}

// ✅ Applique couleur fill
function setFill(doc, rgb) {
  doc.setFillColor(rgb[0], rgb[1], rgb[2])
}

// ✅ Applique couleur draw (stroke) — séparé de setFill pour éviter confusion
function setDraw(doc, rgb) {
  doc.setDrawColor(rgb[0], rgb[1], rgb[2]) // ✅ args séparés, pas de spread
}

// ✅ Ajoute image avec fallback placeholder
function safeAddImage(doc, dataUrl, x, y, w, h) {
  if (!dataUrl) {
    setFill(doc, COLORS.lightGray)
    doc.rect(x, y, w, h, 'F')
    doc.setTextColor(COLORS.midGray[0], COLORS.midGray[1], COLORS.midGray[2])
    doc.setFontSize(8)
    doc.text('Pas de photo', x + w / 2, y + h / 2, { align: 'center' })
    return
  }
  try {
    doc.addImage(dataUrl, getImgFormat(dataUrl), x, y, w, h, undefined, 'FAST')
  } catch {
    setFill(doc, COLORS.lightGray)
    doc.rect(x, y, w, h, 'F')
    doc.setTextColor(COLORS.midGray[0], COLORS.midGray[1], COLORS.midGray[2])
    doc.setFontSize(8)
    doc.text('Photo indisponible', x + w / 2, y + h / 2, { align: 'center' })
  }
}

// ✅ Vérifie saut de page
function checkPageBreak(doc, y, needed, margin) {
  if (y + needed > 280) {
    doc.addPage()
    return margin
  }
  return y
}

// ✅ Tronque un texte long avec ellipse pour jsPDF
function truncateText(doc, text, maxWidth) {
  const str = String(text || '')
  if (doc.getTextWidth(str) <= maxWidth) return str
  let truncated = str
  while (truncated.length > 0 && doc.getTextWidth(truncated + '...') > maxWidth) {
    truncated = truncated.slice(0, -1)
  }
  return truncated + '...'
}

// ── Export principal ──────────────────────────────────────────────────────────
export async function generatePDF(inspection) {
  const {
    contract, type, photos, damages, fuel, mileage,
    signatureAgent, signatureClient, completedAt, departPhotos,
  } = inspection

  const safeDate   = completedAt ? new Date(completedAt) : new Date()
  const driverName = contract?.driverName || contract?.conductorName

  const { default: jsPDF } = await import('jspdf')
  const doc      = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const W        = 210
  const margin   = 12
  const contentW = W - margin * 2
  let y          = margin

  // ── 1. Header ──────────────────────────────────────────────────────────────
  setFill(doc, COLORS.primary)
  doc.rect(0, 0, W, 30, 'F')

  // Bandeau coloré latéral selon type
  setFill(doc, type === 'depart' ? COLORS.success : COLORS.warning)
  doc.rect(0, 0, 4, 30, 'F')

  doc.setTextColor(COLORS.white[0], COLORS.white[1], COLORS.white[2])
  doc.setFontSize(18)
  doc.setFont('helvetica', 'bold')
  doc.text('ÉTAT DES LIEUX VÉHICULE', W / 2, 12, { align: 'center' })

  doc.setFontSize(11)
  doc.setFont('helvetica', 'normal')
  doc.text(
    `${type === 'depart' ? '> DEPART' : '< RETOUR'} \u2014 ${safeDate.toLocaleDateString('fr-FR', { dateStyle: 'long' })}`,
    W / 2, 22, { align: 'center' }
  )

  doc.setTextColor(0, 0, 0)
  y = 38

  // ── 2. Infos contrat ───────────────────────────────────────────────────────
  const infoBox = (label, value, x, w = contentW / 2) => {
    doc.setFontSize(7.5)
    doc.setTextColor(COLORS.textLight[0], COLORS.textLight[1], COLORS.textLight[2])
    doc.setFont('helvetica', 'normal')
    doc.text(label.toUpperCase(), x, y)
    doc.setFontSize(10.5)
    doc.setTextColor(0, 0, 0)
    doc.setFont('helvetica', 'bold')
    // ✅ Tronque si trop long
    const txt = truncateText(doc, String(value ?? '\u2014'), w - 4)
    doc.text(txt, x, y + 5)
  }

  const half = contentW / 2

  infoBox('Immatriculation', contract?.plate,       margin)
  infoBox('Véhicule',        contract?.vehicleName, margin + half)
  y += 14

  infoBox('Client',     contract?.clientName, margin)
  infoBox('Conducteur', driverName,           margin + half)
  y += 14

  infoBox('Date de départ', fmtDT(contract?.startDate), margin)
  if (type === 'retour') {
    infoBox('Date de retour réel',   fmtDT(safeDate),          margin + half)
  } else {
    infoBox('Date de retour prévue', fmtDT(contract?.endDate), margin + half)
  }
  y += 14

  infoBox('N° Contrat', contract?.numeroDocument || contract?.id, margin)

  // ✅ fuel est maintenant une valeur entre 0 et 100 (%)
  const fuelLabel = fuel != null ? `${fuel}%` : '\u2014'
  infoBox('Carburant', fuelLabel, margin + half)
  y += 14

  // ✅ mileage = 0 doit afficher '0 km', pas '—'
  const mileageLabel = mileage != null ? `${mileage} km` : '\u2014'
  infoBox('Kilométrage', mileageLabel, margin)

  const safePhotos  = photos && typeof photos === 'object' ? photos : {}
  const photosDone  = Object.keys(safePhotos).length
  const photosTotal = PHOTO_SLOTS.length
  infoBox(
    `Photos (${photosDone}/${photosTotal})`,
    `${Math.round((photosDone / photosTotal) * 100)}% complété`,
    margin + half
  )
  y += 14

  setDraw(doc, [220, 220, 220])
  doc.line(margin, y, W - margin, y)
  y += 8

  // ── 3. Section Photos ──────────────────────────────────────────────────────
  doc.setFontSize(13)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(COLORS.primary[0], COLORS.primary[1], COLORS.primary[2])
  doc.text('PHOTOS', margin, y)
  y += 7

  const safeDepartPhotos = departPhotos && typeof departPhotos === 'object' ? departPhotos : {}

  if (type === 'retour' && Object.keys(safeDepartPhotos).length > 0) {
    // ── Mode retour : départ / retour côte à côte ────────────────────────────
    const photoW = (contentW - 6) / 2
    const photoH = photoW * 0.65

    for (const slot of PHOTO_SLOTS) {
      y = checkPageBreak(doc, y, photoH + 22, margin)

      const departData = resolveDataUrl(safeDepartPhotos[slot.id])
      const retourData = resolveDataUrl(safePhotos[slot.id])

      doc.setFontSize(9)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(COLORS.darkGray[0], COLORS.darkGray[1], COLORS.darkGray[2])
      doc.text(slot.label, margin, y + 4)
      y += 6

      const xDepart = margin
      const xRetour = margin + photoW + 6

      // En-tête DÉPART
      setFill(doc, COLORS.success)
      doc.rect(xDepart, y, photoW, 5, 'F')
      doc.setTextColor(COLORS.white[0], COLORS.white[1], COLORS.white[2])
      doc.setFontSize(7)
      doc.setFont('helvetica', 'bold')
      doc.text('DEPART', xDepart + photoW / 2, y + 3.5, { align: 'center' })

      // En-tête RETOUR
      setFill(doc, COLORS.warning)
      doc.rect(xRetour, y, photoW, 5, 'F')
      doc.text('RETOUR', xRetour + photoW / 2, y + 3.5, { align: 'center' })
      y += 5

      safeAddImage(doc, departData, xDepart, y, photoW, photoH)
      safeAddImage(doc, retourData, xRetour, y, photoW, photoH)

      // Badge dommage
      const hasDamage = (damages || []).some(d => d.slotId === slot.id)
      if (hasDamage) {
        setFill(doc, COLORS.danger)
        doc.circle(xRetour + photoW - 4, y + 4, 3, 'F')
        doc.setTextColor(COLORS.white[0], COLORS.white[1], COLORS.white[2])
        doc.setFontSize(6)
        doc.text('!', xRetour + photoW - 4, y + 5.5, { align: 'center' })
      }

      y += photoH + 10
    }

  } else {
    // ── Mode départ : grille 2 colonnes ─────────────────────────────────────
    const cols   = 2
    const imgW   = (contentW - 4) / cols
    const imgH   = imgW * 0.65
    const labelH = 6
    let col      = 0

    for (const slot of PHOTO_SLOTS) {
      // ✅ Vérification saut de page AVANT de placer l'image
      y = checkPageBreak(doc, y, imgH + labelH + 10, margin)
      const x = margin + col * (imgW + 4)

      safeAddImage(doc, resolveDataUrl(safePhotos[slot.id]), x, y, imgW, imgH)

      setFill(doc, COLORS.primary)
      doc.rect(x, y + imgH, imgW, labelH, 'F')
      doc.setTextColor(COLORS.white[0], COLORS.white[1], COLORS.white[2])
      doc.setFontSize(7)
      doc.setFont('helvetica', 'bold')
      doc.text(slot.label, x + 2, y + imgH + 4.5)

      // Badge dommage
      const hasDamage = (damages || []).some(d => d.slotId === slot.id)
      if (hasDamage) {
        setFill(doc, COLORS.danger)
        doc.circle(x + imgW - 4, y + 4, 3, 'F')
        doc.setTextColor(COLORS.white[0], COLORS.white[1], COLORS.white[2])
        doc.setFontSize(6)
        doc.text('!', x + imgW - 4, y + 5.5, { align: 'center' })
      }

      col++
      if (col >= cols) {
        col = 0
        y += imgH + labelH + 10
      }
    }
    // ✅ Flush dernière ligne incomplète
    if (col > 0) y += imgH + labelH + 10
  }

  // ── 4. Tableau dommages ────────────────────────────────────────────────────
  if (Array.isArray(damages) && damages.length > 0) {
    y = checkPageBreak(doc, y, 20, margin)

    doc.setFontSize(13)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(COLORS.primary[0], COLORS.primary[1], COLORS.primary[2])
    doc.text('RELEVÉ DES DOMMAGES', margin, y)
    y += 7

    // En-tête tableau
    setFill(doc, COLORS.primary)
    doc.rect(margin, y, contentW, 7, 'F')
    doc.setTextColor(COLORS.white[0], COLORS.white[1], COLORS.white[2])
    doc.setFontSize(8)
    doc.setFont('helvetica', 'bold')
    doc.text('N°',          margin + 2,   y + 5)
    doc.text('Zone',        margin + 14,  y + 5)
    doc.text('Description', margin + 70,  y + 5)
    doc.text('Date',        margin + 145, y + 5)
    y += 7

    damages.forEach((d, i) => {
      y = checkPageBreak(doc, y, 7, margin)
      setFill(doc, i % 2 === 0 ? COLORS.rowEven : COLORS.white)
      doc.rect(margin, y, contentW, 7, 'F')
      doc.setTextColor(0, 0, 0)
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')

      const zoneLabel = PHOTO_SLOTS.find(s => s.id === d.slotId)?.label || '\u2014'
      // ✅ Tronque la description si trop longue
      const descLabel = truncateText(doc, d.label || 'Non décrit', 70)
      const dateLabel = d.createdAt ? fmtDT(d.createdAt).slice(0, 10) : '\u2014'

      doc.text(String(i + 1), margin + 2,   y + 5)
      doc.text(zoneLabel,     margin + 14,  y + 5)
      doc.text(descLabel,     margin + 70,  y + 5)
      doc.text(dateLabel,     margin + 145, y + 5)
      y += 7
    })
    y += 6
  }

  // ── 5. Signatures ──────────────────────────────────────────────────────────
  y = checkPageBreak(doc, y, 65, margin)

  doc.setFontSize(13)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(COLORS.primary[0], COLORS.primary[1], COLORS.primary[2])
  doc.text('SIGNATURES', margin, y)
  y += 8

  const sigW = (contentW - 6) / 2
  const sigH = 35

  ;[
    { label: "Signature de l'agent", data: signatureAgent  },
    { label: 'Signature du client',  data: signatureClient },
  ].forEach((s, i) => {
    const sx = margin + i * (sigW + 6)
    setDraw(doc, [200, 200, 200])
    doc.rect(sx, y, sigW, sigH)
    doc.setFontSize(8)
    doc.setTextColor(COLORS.textLight[0], COLORS.textLight[1], COLORS.textLight[2])
    doc.text(s.label, sx + sigW / 2, y + 4, { align: 'center' })
    if (s.data) {
      try {
        // ✅ Détecte le format réel de la signature (PNG ou JPEG)
        const sigFormat = getImgFormat(s.data)
        doc.addImage(s.data, sigFormat, sx + 4, y + 6, sigW - 8, sigH - 10)
      } catch (e) {
        console.warn('Signature image échouée:', e.message)
      }
    }
    doc.setFontSize(7)
    doc.setTextColor(COLORS.midGray[0], COLORS.midGray[1], COLORS.midGray[2])
    doc.text(safeDate.toLocaleString('fr-FR'), sx + sigW / 2, y + sigH - 2, { align: 'center' })
  })

  y += sigH + 6

  // ✅ Bandeau mention archivage MRC — sans emoji (non supporté par jsPDF)
  setFill(doc, COLORS.rowEven)
  doc.rect(margin, y, contentW, 10, 'F')
  setDraw(doc, COLORS.midGray)
  doc.rect(margin, y, contentW, 10)
  doc.setFontSize(7.5)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(COLORS.textLight[0], COLORS.textLight[1], COLORS.textLight[2])
  doc.text(
    `[PJ] Photos originales archivees dans le dossier MyRentCar (contrat n° ${contract?.numeroDocument || contract?.id || '\u2014'})`,
    margin + 3, y + 6.5
  )
  y += 14

  // ── 6. Footer sur toutes les pages ────────────────────────────────────────
  const pageCount = doc.getNumberOfPages()
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p)
    doc.setFontSize(7)
    doc.setTextColor(COLORS.midGray[0], COLORS.midGray[1], COLORS.midGray[2])
    doc.text(
      `Olympic Location \u2014 EDL ${type === 'depart' ? 'Depart' : 'Retour'} \u2014 ${contract?.plate || ''} \u2014 Page ${p}/${pageCount}`,
      W / 2, 293, { align: 'center' }
    )
    // ✅ setDrawColor avec args séparés
    doc.setDrawColor(COLORS.lightGray[0], COLORS.lightGray[1], COLORS.lightGray[2])
    doc.line(margin, 290, W - margin, 290)
  }

  // ✅ Métadonnées PDF
  doc.setProperties({
    title:    `EDL ${type} - ${contract?.plate || ''}`,
    subject:  `Etat des lieux ${type === 'depart' ? 'depart' : 'retour'}`,
    author:   'Olympic Location',
    keywords: `edl,${type},${contract?.plate || ''}`,
    creator:  'RentalCheck App',
  })

  const pdfBlob   = doc.output('blob')
  const pdfBase64 = doc.output('datauristring').split(',')[1]
  return { pdfBlob, pdfBase64 }
}